import { getCrops } from '@next/arable-lib/crop';
import { RouterSelectors } from '@next/core-lib/router-store';
import { createSelector, createFeatureSelector } from '@ngrx/store';

import { getCropFields } from 'src/app/registration/store/selectors/crop-field.selectors';

import { SoilAnalysisState, SoilAnalysis } from '../models/soil-analysis.model';

export const selectSoilAnalysisState = createFeatureSelector<SoilAnalysisState>('soilAnalysis');

export const selectSoilAnalyses = createSelector(
  selectSoilAnalysisState,
  (state) => state?.soilAnalyses || [],
);

export const selectDefaultSoilAnalyses = createSelector(
  selectSoilAnalysisState,
  (state) => state?.defaultSoilAnalyses || [],
);

export const selectSoilAnalysisLoading = createSelector(
  selectSoilAnalysisState,
  (state) => state?.isLoading || false,
);

export const selectSelectedFeatureIds = createSelector(
  selectSoilAnalysisState,
  (state) => state?.selectedFeatureIds || [],
);

export const selectAllFeatureIds = createSelector(selectSoilAnalyses, (soilAnalyses) =>
  soilAnalyses
    .filter((soilAnalysis) => soilAnalysis.borderData)
    .map(({ borderData }) => borderData.id),
);

export const selectSoilAnalysisById = ({ soilAnalysisId }: { soilAnalysisId: number }) =>
  createSelector(selectSoilAnalyses, (soilAnalyses: SoilAnalysis[]) =>
    soilAnalyses.find(({ id }) => id === soilAnalysisId),
  );

export const selectSoilAnalysisByFeatureId = ({ featureId }: { featureId: string }) =>
  createSelector(selectSoilAnalyses, (soilAnalyses: SoilAnalysis[]) =>
    soilAnalyses.find(({ borderData }) => borderData?.id === featureId),
  );

export const selectLatestSoilAnalysisByFeatures = ({ featureIds }: { featureIds: string[] }) =>
  createSelector(selectSoilAnalyses, (soilAnalyses: SoilAnalysis[]) => {
    const soilAnalysesWithFeatures = soilAnalyses.filter(
      ({ borderData }) => borderData && featureIds.includes(borderData.id),
    );
    return soilAnalysesWithFeatures.sort(
      (a, b) =>
        new Date(b.receiptDateDateTime).getTime() - new Date(a.receiptDateDateTime).getTime(),
    )[0];
  });

export const selectCropFieldIdsToReportSoilAnalysis = createSelector(
  RouterSelectors.getRouteParam('cropFieldsIdsReport'),
  getCropFields,
  (cropFieldsIdsReport, cropFields) =>
    cropFieldsIdsReport
      ? (JSON.parse(cropFieldsIdsReport) as number[])
      : cropFields.map((cropField) => cropField.id),
);

export const selectCharacteristicIdsToReportSoilAnalysis = createSelector(
  RouterSelectors.getRouteParam('characteristicIds'),
  (characteristicIds) => (characteristicIds ? (JSON.parse(characteristicIds) as number[]) : []),
);

export const selectSoilAnalysesReport = createSelector(
  getCropFields,
  selectCropFieldIdsToReportSoilAnalysis,
  selectCharacteristicIdsToReportSoilAnalysis,
  selectSoilAnalyses,
  selectDefaultSoilAnalyses,
  getCrops,
  (
    cropFields,
    cropFieldsIdsReport,
    characteristicIdsReport,
    soilAnalyses,
    defaultSoilAnalyses,
    crops,
  ) => {
    const cropFieldsToReport = cropFields.filter((cropField) =>
      cropFieldsIdsReport.includes(cropField.id),
    );

    return cropFieldsToReport
      .map((cropField) => {
        const filteredSoilAnalyses = soilAnalyses
          .concat(defaultSoilAnalyses)
          .filter((soilAnalysis) => cropField.soilAnalysisIds.includes(soilAnalysis.id));
        return {
          ...cropField,
          soilAnalyses: filteredSoilAnalyses.map((analysis) => ({
            ...analysis,
            sampledCharacteristics: analysis.sampledCharacteristics.filter(
              (characteristic) =>
                !characteristicIdsReport.length ||
                characteristicIdsReport.indexOf(characteristic.id) !== -1,
            ),
          })),
          cropName: !crops?.length ? '' : crops.find((crop) => crop.id === cropField.cropId)?.name,
        };
      })
      .filter((cropField) => cropField.soilAnalyses.length);
  },
);
